import React, { useContext, useMemo, useState } from 'react';
import Modal from '../../modal/Modal';
import { ModalContent, ModalSize, ModalSubhead } from '../../modal';
import ModalHeader from '../../modal/ModalHeader';
import ModalFooter from '../../modal/ModalFooter';
import StyledLabel from '../../label';
import Input from '../../input/Input';
import { useDebounce } from '../../hooks';
import { IconPosition, LoadingSize, Spinner } from 'venn-ui-kit';
import Field from '../../modals/validation/Field';
import UserContext from '../../contexts/user-context';
import { useValidatePrivatePortfolioName } from '../../utils/useValidatePrivatePortfolioName';
import { useHasFF } from 'venn-utils';
import { OwnershipContextSelector } from '../../modals/components/OwnershipContextSelector';
import { isNil } from 'lodash';

interface SavePrivatePortfolioAsModalProps {
  onClose: () => void;
  onSave: (newName: string, ownerContextId: string | undefined) => void;
}

export const SavePrivatePortfolioAsModal = ({ onClose, onSave }: SavePrivatePortfolioAsModalProps) => {
  const { defaultContext } = useContext(UserContext);
  const [name, setName] = useState<string>('');
  const [debouncedName] = useDebounce(name, 500);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [nameError, setNameError] = useState<string>('');
  const [ownerContextId, setOwnerContextId] = useState<string | undefined>(defaultContext);

  const hasNewPermissions = useHasFF('new_permissions_ff');

  useValidatePrivatePortfolioName(
    debouncedName,
    useMemo(
      () => ({
        onEmpty: () => setNameError(''),
        onValid: () => setNameError(''),
        onInvalid: (error: string) => setNameError(error),
        setIsLoading: (isLoading: boolean) => setIsLoading(isLoading),
      }),
      [],
    ),
  );

  const invalidOwnerContextId = hasNewPermissions && isNil(ownerContextId);

  return (
    <Modal
      closeOnOutsideClick
      onClose={onClose}
      size={ModalSize.Large}
      closeOnEsc
      className="qa-save-private-portfolio-as-modal"
    >
      <ModalHeader>Save As...</ModalHeader>
      <ModalSubhead>Please enter a name for your portfolio</ModalSubhead>
      <ModalContent>
        <Field className="qa-input-portfolio-name" error={nameError}>
          <div className="flex flex-col">
            <StyledLabel required>Portfolio name:</StyledLabel>
            <Input
              width={580}
              placeholder="Select unique portfolio name"
              value={name}
              onChange={setName}
              icon={isLoading ? <Spinner size={LoadingSize.micro} /> : undefined}
              iconPosition={IconPosition.Right}
            />
          </div>
        </Field>
        {hasNewPermissions && (
          <OwnershipContextSelector
            ownerContextId={ownerContextId}
            setOwnerContextId={setOwnerContextId}
            requiredAction="CREATE_PORTFOLIO"
          />
        )}
      </ModalContent>
      <ModalFooter
        onCancel={onClose}
        primaryLabel="Save"
        onPrimaryClick={() => onSave(name, ownerContextId)}
        primaryDisabled={isLoading || !name || !!nameError || invalidOwnerContextId}
      />
    </Modal>
  );
};
